'use client';

import { TeamLeagueCardv2 } from '@pickleballinc/features/src/global/cards/TeamLeagueCardv2';
import { formatLocation } from '@pickleballinc/lib/format-location';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { TeamLeagueCard } from '@pickleballinc/ui/components/global/Cards/TeamLeagueCard';
import { MyModulesNoResults } from '@pickleballinc/ui/components/global/myModules/MyModulesNoResults';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { PaginationWrapper } from '@/components/atoms';
import { League } from '@/entities/League';
import { FEATURE_FLAGS } from '@/hooks/constants';
import { usePaginationPageChange } from '@/hooks/custom-hooks/usePaginationPageChange';
import { useTeamLeagueFavorite } from '@/hooks/hooks';
import { useFavoriteLeagueMutation } from '@/hooks/league/useFavoriteLeagueMutation';
import { publicEnv } from '@/lib/env';
import { MyTeamLeaguesTypes } from '@/utils/types';

interface PlayerLeaguesProps {
	leagues: League[];
	totalRecords: number;
	pageSize: number;
	currentPage: number;
}

export const PlayerLeaguesList = ({ leagues, totalRecords, pageSize, currentPage }: PlayerLeaguesProps) => {
	const searchParams = useSearchParams();
	const { session } = useSession();
	const isUserLogged = !!session?.user?.uuid;
	const { handlePageChange } = usePaginationPageChange();

	const [activeFavoriteUuid, setActiveFavoriteUuid] = useState<string | null>(null);

	const { addToFavorites, removeFromFavorites, isAdding, isRemoving } = useFavoriteLeagueMutation();

	const teamLeagueType = searchParams?.get('team_league_type');
	const isManaging = teamLeagueType === MyTeamLeaguesTypes.Managing;
	const isFavorite = teamLeagueType === MyTeamLeaguesTypes.Favorited;
	const hasSearch = !!searchParams?.get('search');

	const handleFavoriteToggle = (event: React.MouseEvent, isFavorite: boolean, uuid: string) => {
		event.stopPropagation();
		event.preventDefault();

		const mutation = isFavorite ? removeFromFavorites : addToFavorites;

		if (isAdding || isRemoving) return;
		setActiveFavoriteUuid(uuid);

		mutation.mutate(
			{ moduleUuid: uuid },
			{
				onSettled: () => {
					setActiveFavoriteUuid(null);
				}
			}
		);
	};

	const noResultsTitle = hasSearch
		? 'No Team Leagues Found'
		: isManaging
			? 'Ready to Manage a Team League?'
			: isFavorite
				? 'Favorite a Team League'
				: 'Ready to Join a Team League?';
	const noResultsDescription = hasSearch
		? 'Try adjusting your search or filters and give it another go.'
		: isManaging
			? 'Create a team league and compete with others now!'
			: isFavorite
				? 'Explore all the team leagues around you and add them to your favorites'
				: 'Find team leagues that match your skill level and start competing.';
	const noResultsButtonText = isManaging ? 'Create a Team League' : 'Explore Team Leagues';

	return leagues.length !== 0 ? (
		<div className="flex flex-wrap justify-center gap-6 lg:justify-normal">
			{leagues.map((league: League) =>
				FEATURE_FLAGS.ENABLE_NEW_CARDS ? (
					<TeamLeagueCardv2
						key={league.uuid}
						{...league}
						id={league.uuid}
						logo={league.logo?.url}
						location={
							league.location
								? formatLocation([league.location.city, league.location.stateInfo.title, league.location.countryInfo.abbreviation])
								: undefined
						}
						teamLeaguesBaseUrl={publicEnv.NEXT_PUBLIC_PTL_URL}
						useFavoriteState={useTeamLeagueFavorite}
						showFavoriteIcon
						initialIsFavorite={league.isFavourite}
					/>
				) : (
					<TeamLeagueCard
						key={league.uuid}
						league={league}
						teamLeaguesBaseUrl={publicEnv.NEXT_PUBLIC_PTL_URL}
						isUserLogged={isUserLogged}
						handleFavoriteToggle={(e) => handleFavoriteToggle(e, league.isFavourite, league.uuid)}
						isAddingLeagueToFavorite={activeFavoriteUuid === league.uuid}
					/>
				)
			)}

			{totalRecords > pageSize && (
				<div className="mx-auto w-full max-w-screen-2xl px-6 bg-white py-3 rounded-lg mt-6">
					<PaginationWrapper
						currentPage={currentPage}
						totalPages={Math.ceil(totalRecords / pageSize)}
						onPageChange={handlePageChange}
						className="flex justify-between w-full"
					/>
				</div>
			)}
		</div>
	) : (
		<div className="mx-auto max-w-[442px] py-8">
			<MyModulesNoResults
				text={noResultsTitle}
				description={noResultsDescription}
				buttonText={noResultsButtonText}
				buttonLink={publicEnv.NEXT_PUBLIC_PTL_URL}
				buttonClasses="!bg-darkorange-400"
				illustrationUrl={getCDNURL(`${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/empty-dataset/PTL-empty-dataset-alt.svg`, 320, 233)}
				cdnUrl={publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}
			/>
		</div>
	);
};
